import { z } from 'zod'

/**
 * The onboarding vocabularies live here rather than in the planner so a value
 * and its validator cannot drift apart. The planner imports these tuples and
 * attaches Czech labels, descriptions and images; the API validates against the
 * enums derived from them. Adding an option means touching one list.
 */

export const DATE_MODES = ['known', 'searching'] as const

export const SEASONS = ['jaro', 'leto', 'podzim', 'zima', 'nevim'] as const

export const REGIONS = [
  'Praha',
  'Středočeský kraj',
  'Jihočeský kraj',
  'Plzeňský kraj',
  'Karlovarský kraj',
  'Ústecký kraj',
  'Liberecký kraj',
  'Královéhradecký kraj',
  'Pardubický kraj',
  'Kraj Vysočina',
  'Jihomoravský kraj',
  'Olomoucký kraj',
  'Zlínský kraj',
  'Moravskoslezský kraj',
] as const

export const WEDDING_STYLES = [
  'rustikalni',
  'elegantni',
  'boho',
  'vintage',
  'minimalisticky',
  'zahradni',
] as const

export const VENUE_TYPES = [
  'zamek',
  'stodola',
  'vinice',
  'zahrada',
  'hotel',
  'priroda',
  'loft',
  'plaz',
] as const

export const DAY_ACTIVITIES = [
  'prvni-tanec',
  'ohnostroj',
  'ziva-kapela',
  'fotokoutek',
  'candy-bar',
  'raut',
  'ohniva-show',
  'dort',
] as const

export const BUDGET_BANDS = ['do-250', '250-400', '400-600', '600-1000', '1000-plus'] as const

export const dateModeSchema = z.enum(DATE_MODES)
export const seasonSchema = z.enum(SEASONS)
export const regionSchema = z.enum(REGIONS)
export const weddingStyleSchema = z.enum(WEDDING_STYLES)
export const venueTypeSchema = z.enum(VENUE_TYPES)
export const dayActivitySchema = z.enum(DAY_ACTIVITIES)
export const budgetBandSchema = z.enum(BUDGET_BANDS)

export const GUEST_COUNT_MIN = 1
export const GUEST_COUNT_MAX = 2000

const coupleSchema = z.object({
  brideFirstName: z.string().trim().min(1, 'Enter the bride first name').max(100),
  brideLastName: z.string().trim().min(1, 'Enter the bride last name').max(100),
  groomFirstName: z.string().trim().min(1, 'Enter the groom first name').max(100),
  groomLastName: z.string().trim().min(1, 'Enter the groom last name').max(100),
})

const eventSchema = z.object({
  dateMode: dateModeSchema,
  weddingDate: z.iso.date().optional(),
  season: seasonSchema.optional(),
  region: regionSchema,
  guestCount: z.number().int().min(GUEST_COUNT_MIN).max(GUEST_COUNT_MAX),
})

const preferencesSchema = z.object({
  styles: z.array(weddingStyleSchema).default([]),
  customStyle: z.string().trim().max(500).optional(),
  venues: z.array(venueTypeSchema).default([]),
  customVenue: z.string().trim().max(500).optional(),
  activities: z.array(dayActivitySchema).default([]),
  customActivities: z.string().trim().max(500).optional(),
  budget: budgetBandSchema.optional(),
})

/**
 * The whole wizard in one payload. The current onboarding only asks for the
 * couple and their wedding brief; the event block remains optional for older
 * clients and future setup flows.
 */
export const onboardingSubmissionSchema = z
  .object({
    couple: coupleSchema,
    event: eventSchema.optional(),
    preferences: preferencesSchema,
  })
  .superRefine((value, ctx) => {
    if (value.event) {
      // The legacy wizard showed a date picker or a season picker, never both.
      if (value.event.dateMode === 'known' && !value.event.weddingDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['event', 'weddingDate'],
          message: 'Pick the wedding date',
        })
      }

      if (value.event.dateMode === 'searching' && !value.event.season) {
        ctx.addIssue({
          code: 'custom',
          path: ['event', 'season'],
          message: 'Pick a preferred season',
        })
      }
    }

    // A freehand style stands in for the presets, so either one satisfies the step.
    if (value.preferences.styles.length === 0 && !value.preferences.customStyle) {
      ctx.addIssue({
        code: 'custom',
        path: ['preferences', 'styles'],
        message: 'Pick a style or describe your own',
      })
    }

    if (value.preferences.venues.length === 0 && !value.preferences.customVenue) {
      ctx.addIssue({
        code: 'custom',
        path: ['preferences', 'venues'],
        message: 'Pick a venue type or describe your own',
      })
    }

    if (value.preferences.activities.length === 0 && !value.preferences.customActivities) {
      ctx.addIssue({
        code: 'custom',
        path: ['preferences', 'activities'],
        message: 'Pick an activity or describe your own',
      })
    }
  })

export type DateMode = (typeof DATE_MODES)[number]
export type Season = (typeof SEASONS)[number]
export type Region = (typeof REGIONS)[number]
export type WeddingStyle = (typeof WEDDING_STYLES)[number]
export type VenueType = (typeof VENUE_TYPES)[number]
export type DayActivity = (typeof DAY_ACTIVITIES)[number]
export type BudgetBand = (typeof BUDGET_BANDS)[number]
export type OnboardingSubmission = z.infer<typeof onboardingSubmissionSchema>
